import { HttpClientModule } from '@angular/common/http';
import { isDevMode, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AuthComponentsModule, AuthGmsAngularClientModule } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import {
  AppEnvironment,
  CustomTranslationLoader,
  provideTranslateDebugStore
} from '@coin/shared/core/data-access-translation-loader';
import {
  IconButtonComponent,
  UpdateNotifierComponent,
  V2ButtonComponent,
  V2TextInputComponent
} from '@coin/shared/core/ui-storybook';
import { environment } from '@coin/shared/core/util-environments';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { ToastrModule } from 'ngx-toastr';
import { AppState, UiModule } from '@coin/importer/core/feature';
import { SimplebarAngularModule } from 'simplebar-angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';
import { httpInterceptorProviders } from './auth/http-interceptors';
import { ButtonFooterComponent } from './components/button-footer/button-footer.component';
import { ChartsComponent } from './components/charts/charts.component';
import { ExportAttributesFormComponent } from './components/export-attributes-form/export-attributes-form.component';
import { ExportAttributesPopupComponent } from './components/export-attributes-popup/export-attributes-popup.component';
import { FileTypePopupComponent } from './components/file-type-popup/file-type-popup.component';
import { FiletypeOverviewComponent } from './components/filetype-overview/filetype-overview.component';
import { HeaderComponent } from './components/header/header.component';
import { ImportDecisionComponent } from './components/import-decision/import-decision.component';
import { ImportDetailsComponent } from './components/import-details/import-details.component';
import { ObjectRefresherPopupComponent } from './components/object-refresher-popup/object-refresher-popup.component';
import { EmailListEntryPopupComponent } from './components/organisation-management/email-list-entry-popup/email-list-entry-popup.component';
import { EmailListComponent } from './components/organisation-management/email_list/email_list.component';
import { StructureListEntryPopupComponent } from './components/organisation-management/structure-list-entry-popup/structure-list-entry-popup.component';
import { StructureListComponent } from './components/organisation-management/structure_list/structure_list.component';
import { TransferListEntryPopupComponent } from './components/organisation-management/transfer-list-entry-popup/transfer-list-entry-popup.component';
import { TransferListComponent } from './components/organisation-management/transfer_list/transfer_list.component';
import { PopupComponent } from './components/popup/popup.component';
import { SchemaValidationsPopupComponent } from './components/schema-validations-popup/schema-validations-popup.component';
import { SimpleTableComponent } from './components/simple-table/simple-table.component';
import { SkipPreviousImportsPopupComponent } from './components/skip-previous-imports-popup/skip-previous-imports-popup.component';
import { SubHeaderComponent } from './components/sub-header/sub-header.component';
import { TransactionStateProgressBarComponent } from './components/transaction-state-progress-bar/transaction-state-progress-bar.component';
import { UploadPopupComponent } from './components/upload-popup/upload-popup.component';
import { ValidationListComponent } from './components/validation-list/validation-list.component';
import { DemoMaterialModule } from './material.module';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { OrganisationManagementComponent } from './pages/organisation-management/organisation_management.component';
import { RepublishComponent } from './pages/republish/republish.component';
import { RepublishSummaryComponent } from './pages/republish_summary/republish_summary.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { SummaryComponent } from './pages/summary/summary.component';
import { DurationFromMsPipe } from './pipes/duration-from-ms/duration-from-ms.pipe';
import { FileNamePipe } from './pipes/file-name/file-name.pipe';
import { FileSizePipe } from './pipes/file-size/file-size.pipe';
import { PercentagePipe } from './pipes/percentage/percentage.pipe';
import { LogsService } from './services/logs/logs.service';
import { EmailListService } from './services/organisation-management/email-list.service';
import { StructureListService } from './services/organisation-management/structure-list.service';
import { TransferListService } from './services/organisation-management/transfer-list.service';
import { UploadService } from './services/upload/upload.service';
import { TOAST_OPTIONS, ToastService } from './services/toast/toast.service';
import { ApiModule, Configuration } from '@coin/shared/core/data-access';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(ROUTES),
    HttpClientModule,
    ApiModule.forRoot(() => new Configuration({ basePath: environment.api.baseUrl })),
    FormsModule,
    ReactiveFormsModule,
    MatSliderModule,
    DemoMaterialModule,
    UiModule,
    SimplebarAngularModule,
    ToastrModule.forRoot(TOAST_OPTIONS),
    AuthComponentsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWithDelay:5000'
    }),
    AuthGmsAngularClientModule.forRoot({
      ...environment.authentication,
      ...environment.importerAuthentication
    } as AuthGmsClientConfig),
    NgxsModule.forRoot(AppState, {
      developmentMode: !environment.production,
      selectorOptions: { suppressErrors: true }
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production
    }),
    TranslateModule.forRoot(CustomTranslationLoader.createConfig(AppEnvironment.Admin)),
    BaseChartDirective,
    UpdateNotifierComponent,
    V2ButtonComponent,
    IconButtonComponent,
    V2TextInputComponent
  ],
  declarations: [
    AppComponent,
    DashboardComponent,
    RepublishComponent,
    HeaderComponent,
    SummaryComponent,
    RepublishSummaryComponent,
    FileSizePipe,
    PercentagePipe,
    ChartsComponent,
    DurationFromMsPipe,
    ValidationListComponent,
    FileNamePipe,
    SubHeaderComponent,
    SimpleTableComponent,
    ImportDetailsComponent,
    ImportDecisionComponent,
    PopupComponent,
    ButtonFooterComponent,
    UploadPopupComponent,
    SettingsComponent,
    ExportAttributesPopupComponent,
    ExportAttributesFormComponent,
    FileTypePopupComponent,
    ObjectRefresherPopupComponent,
    FiletypeOverviewComponent,
    SchemaValidationsPopupComponent,
    TransactionStateProgressBarComponent,
    SkipPreviousImportsPopupComponent,
    TransferListComponent,
    TransferListEntryPopupComponent,
    OrganisationManagementComponent,
    StructureListComponent,
    StructureListEntryPopupComponent,
    EmailListComponent,
    EmailListEntryPopupComponent
  ],
  exports: [RouterModule, PercentagePipe, V2ButtonComponent, IconButtonComponent],
  providers: [
    httpInterceptorProviders,
    LogsService,
    ToastService,
    UploadService,
    provideCharts(withDefaultRegisterables()),
    TransferListService,
    StructureListService,
    EmailListService,
    TranslateModule,
    TranslateService,
    provideTranslateDebugStore(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } } // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
